import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { StyledTreeItem } from './styled-drive-tree-item';
import { UseFormSetValue } from 'react-hook-form';
import { getDriveFoldersOwnedByMe, UserKeys } from '../../../../api/users-api';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Messages } from '../../../../utils/message-constants';
import { DriveSweepFormValues } from '../../../drive/drive-sweep/create-drive-sweep/create-drive-sweep-form-page';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import AddToDrive from '@mui/icons-material/AddToDrive';
import FolderIcon from '@mui/icons-material/Folder';
import FolderShared from '@mui/icons-material/FolderShared';

interface DriveFolderOwnedByMeTreeProps {
  userEmail: string;
  selectedFolder: string;
  setSelectedFolder: React.Dispatch<React.SetStateAction<string>>;
  setValue?: UseFormSetValue<DriveSweepFormValues>;
}

export default function DriveFolderOwnedByMeTree({
  userEmail,
  setSelectedFolder,
  selectedFolder,
  setValue,
}: DriveFolderOwnedByMeTreeProps) {
  const theme = useTheme();
  const driveId = 'ownedByMe';
  const [ref, inView] = useInView();

  const { fetchNextPage, isFetchingNextPage, hasNextPage, data } =
    useInfiniteQuery({
      queryKey: [UserKeys.DriveFoldersForOwnedByMe, userEmail],
      queryFn: ({ pageParam }) => {
        return getDriveFoldersOwnedByMe(
          {
            pageSize: 200,
            cursor: pageParam,
          },
          userEmail,
          'name',
          'ASC'
        );
      },
      initialPageParam: '',
      getNextPageParam: (lastPage) => lastPage.cursor,
      meta: {
        errorMessage: Messages.Errors.Network.Loading(
          'drive folders "owned by me"'
        ),
      },
    });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const folderData = data?.pages.flatMap((page) => page.results);

  return (
    <Stack sx={{ flex: 1, py: 0.5 }}>
      <SimpleTreeView
        aria-label="drive folders owned by me"
        slots={{
          expandIcon: ArrowRightIcon,
          collapseIcon: ArrowDropDownIcon,
        }}
        sx={{
          p: 0.25,
        }}
        onSelectedItemsChange={(_event, nodeId) => {
          if (nodeId) {
            setSelectedFolder(nodeId); // Update the selected folder
            const selected = folderData?.find((folder) => folder.id === nodeId);

            if (setValue) {
              setValue('folderName', selected?.title || '');
            }
          }
        }}
        selectedItems={selectedFolder}
        multiSelect={false}
      >
        <StyledTreeItem
          itemId={driveId}
          labelText="Owned by me"
          labelIcon={AddToDrive}
          color={theme.palette.text.primary}
          bgColor={theme.palette.primary.light}
          disabled={userEmail === ''}
        >
          {folderData &&
            folderData.map((folder) => {
              return (
                <StyledTreeItem
                  key={folder.id}
                  itemId={folder.id}
                  labelText={folder.title}
                  labelIcon={
                    folder.iconUrl.includes('shared')
                      ? FolderShared
                      : FolderIcon
                  }
                  labelInfo=""
                  color={theme.palette.text.primary}
                  bgColor={theme.palette.primary.light}
                />
              );
            })}
          {!hasNextPage || isFetchingNextPage ? null : (
            <Button ref={ref} onClick={() => fetchNextPage()}>
              {hasNextPage ? 'Fetch More Folders' : 'No more folders'}
            </Button>
          )}
          {isFetchingNextPage ? (
            <Box sx={{ ml: 6 }}>
              <CircularProgress size={20} />
            </Box>
          ) : null}
        </StyledTreeItem>
      </SimpleTreeView>
    </Stack>
  );
}
